import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalMain,
  Table,
  TableColumns,
} from '@sixfold/common-ui';
import React, { useMemo, useState } from 'react';

import { BigQueryMessage } from './kafka_query_input';
import { FormattedDate } from '../../date_formatting/formatted_date';

import styles from '../css/bigquery.module.css';

interface KafkaTableViewProps {
  payloads: BigQueryMessage[];
}

type KafkaMessageRow = {
  id: string;
  [key: string]: unknown;
};

export const KafkaTableView: React.FC<KafkaTableViewProps> = ({ payloads }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [columnFilter, setColumnFilter] = useState('');
  const [selectedFirstLevelKeys, setSelectedFirstLevelKeys] = useState<Set<string>>(new Set());
  const [hideUnchangedColumns, setHideUnchangedColumns] = useState(false);

  const { allKeys, flattenedMessages, firstLevelKeys, unchangedKeys } = useMemo(() => {
    if (!isOpen) {
      return {
        allKeys: [],
        flattenedMessages: [],
        firstLevelKeys: [],
        unchangedKeys: new Set<string>(),
      };
    }
    const keysSet = new Set<string>();
    const firstLevelKeysSet = new Set<string>();
    const flattened = payloads.map((payload) => {
      const parsedMessage = JSON.parse(payload.message);
      const flatMessage = flattenObject(parsedMessage);

      // Collect all keys
      Object.keys(flatMessage).forEach((key) => {
        keysSet.add(key);
        // Extract first-level key (before first dot or bracket)
        const firstLevelKey = key.split(/[.[]/, 1)[0];
        firstLevelKeysSet.add(firstLevelKey);
      });

      return {
        produced_at: payload.produced_at,
        message_type: payload.message_type,
        flatMessage,
      };
    });

    // Detect columns where all values are the same
    const unchangedKeysSet = new Set<string>();
    if (flattened.length > 1) {
      Array.from(keysSet).forEach((key) => {
        const firstValue = flattened[0].flatMessage[key];
        const allSame = flattened.every((msg) => {
          const value = msg.flatMessage[key];
          // Compare values (handle undefined, null, and different types)
          if (firstValue === undefined && value === undefined) return true;
          if (firstValue === null && value === null) return true;
          return JSON.stringify(firstValue) === JSON.stringify(value);
        });
        if (allSame) {
          unchangedKeysSet.add(key);
        }
      });
    }

    const sortedKeys = Array.from(keysSet).sort();
    const sortedFirstLevelKeys = Array.from(firstLevelKeysSet).sort();

    return {
      allKeys: sortedKeys,
      flattenedMessages: flattened,
      firstLevelKeys: sortedFirstLevelKeys,
      unchangedKeys: unchangedKeysSet,
    };
  }, [isOpen, payloads]);

  const filteredKeys = useMemo(() => {
    let keys = allKeys;

    // Apply unchanged columns filter
    if (hideUnchangedColumns) {
      keys = keys.filter((key) => !unchangedKeys.has(key));
    }

    // Apply first-level key filter
    if (selectedFirstLevelKeys.size > 0) {
      keys = keys.filter((key) => {
        const firstLevelKey = key.split(/[.[]/, 1)[0];
        return selectedFirstLevelKeys.has(firstLevelKey);
      });
    }

    // Apply search filter
    if (columnFilter.trim()) {
      const lowerFilter = columnFilter.toLowerCase();
      keys = keys.filter((key) => key.toLowerCase().includes(lowerFilter));
    }

    return keys;
  }, [allKeys, unchangedKeys, hideUnchangedColumns, selectedFirstLevelKeys, columnFilter]);

  const tableData: KafkaMessageRow[] = useMemo(
    () =>
      flattenedMessages.map((message, index) => ({
        ...message.flatMessage,
        id: String(index),
        producedAt: message.produced_at,
        messageType: message.message_type,
      })),
    [flattenedMessages],
  );

  const tableColumns: TableColumns<KafkaMessageRow> = useMemo(() => {
    const cols: TableColumns<KafkaMessageRow> = [
      {
        id: 'producedAt',
        title: 'Produced At',
        className: styles.bigquery__table_view_sticky_col_first,
        cellClassName: styles.bigquery__table_view_sticky_col_first,
        cellContent: ({ value }) => (
          <span style={{ whiteSpace: 'nowrap' }}>
            <FormattedDate date={value as string} showSecondsAndMillis={true} />
          </span>
        ),
      },
      {
        id: 'messageType',
        title: 'Message Type',
        className: styles.bigquery__table_view_sticky_col_second,
        cellClassName: styles.bigquery__table_view_sticky_col_second,
        cellContent: ({ value }) => <span style={{ whiteSpace: 'nowrap' }}>{value as string}</span>,
      },
      ...filteredKeys.map((key) => ({
        id: key,
        title: <span style={{ whiteSpace: 'nowrap' }}>{key}</span>,
        width: 'auto' as const,
        cellContent: ({
          value,
          rowIndex,
        }: {
          value: unknown;
          rowIndex: number;
          row: KafkaMessageRow;
          context: Record<string, unknown>;
        }) => {
          const previousRow = tableData[rowIndex - 1];
          const previousValue = previousRow?.[key];
          const isDifferent = rowIndex > 0 && JSON.stringify(value) !== JSON.stringify(previousValue);
          return (
            <span style={{ whiteSpace: 'nowrap', fontWeight: isDifferent ? 'bold' : 'normal' }}>
              {value !== undefined ? valueToString(value) : ''}
            </span>
          );
        },
      })),
    ];
    return cols;
  }, [filteredKeys, tableData]);

  if (payloads.length === 0) {
    return null;
  }

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setColumnFilter('');
    setSelectedFirstLevelKeys(new Set());
    setHideUnchangedColumns(false);
  };

  const toggleFirstLevelKey = (key: string) => {
    const newSelection = new Set(selectedFirstLevelKeys);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedFirstLevelKeys(newSelection);
  };

  const selectAllFirstLevelKeys = () => {
    setSelectedFirstLevelKeys(new Set(firstLevelKeys));
  };

  const deselectAllFirstLevelKeys = () => {
    setSelectedFirstLevelKeys(new Set());
  };

  return (
    <>
      <Button kind="primary" size="small" onClick={handleOpen} style={{ marginTop: '10px', marginBottom: '10px' }}>
        Open Table View
      </Button>

      <Modal
        trigger={null}
        open={isOpen}
        onToggle={(nextOpen) => {
          if (!nextOpen) handleClose();
        }}
        size="xlarge"
        className={styles.bigquery__table_view_modal}>
        <ModalHeader>
          <ModalHeading>
            Kafka Messages Table View ({payloads.length} messages)
            {(columnFilter || selectedFirstLevelKeys.size > 0 || hideUnchangedColumns) && (
              <span style={{ marginLeft: '10px', fontSize: '0.9em', color: 'var(--color-neutral-text-subtle)' }}>
                - Showing {filteredKeys.length} of {allKeys.length} columns
              </span>
            )}
          </ModalHeading>
        </ModalHeader>
        <ModalMain padding="none" className={styles.bigquery__table_view_content}>
          {/* Left side - Filters */}
          <div
            style={{
              width: '280px',
              borderRight: '1px solid var(--color-neutral-border-subtle)',
              padding: '20px',
              overflowY: 'auto',
              backgroundColor: 'var(--color-neutral-bg-surface-sunken)',
            }}>
            <h4 style={{ marginTop: 0 }}>Filters</h4>

            {/* Search filter */}
            <div style={{ marginBottom: '20px' }}>
              <Input
                name="column_filter"
                label="Search Columns"
                labelPosition="hidden"
                placeholder="Filter columns..."
                value={columnFilter}
                onChange={({ value }) => setColumnFilter(value)}
              />
            </div>

            {/* Hide unchanged columns filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: 'bold', fontSize: '0.9em', display: 'block', marginBottom: '8px' }}>
                Changed Columns Only
              </label>
              <Checkbox
                label={`Hide unchanged columns (${unchangedKeys.size})`}
                checked={hideUnchangedColumns}
                onChange={({ checked }) => setHideUnchangedColumns(checked ?? false)}
              />
            </div>

            {/* First-level keys filter */}
            <div>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.9em' }}>First-Level Keys</label>
                <div style={{ marginTop: '5px' }}>
                  <Button size="small" onClick={selectAllFirstLevelKeys}>
                    Select All
                  </Button>
                  <Button size="small" onClick={deselectAllFirstLevelKeys} style={{ marginLeft: '5px' }}>
                    Deselect All
                  </Button>
                </div>
              </div>
              <div>
                {firstLevelKeys.map((key) => (
                  <div key={key} style={{ marginBottom: '8px' }}>
                    <Checkbox
                      label={key}
                      checked={selectedFirstLevelKeys.has(key)}
                      onChange={() => toggleFirstLevelKey(key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right side - Table */}
          <div
            className={styles.bigquery__table_view_table_wrapper}
            style={{ flex: 1, overflowX: 'scroll', overflowY: 'auto', position: 'relative' }}>
            <Table data={tableData} columns={tableColumns} />
          </div>
        </ModalMain>
        <ModalFooter>
          <Button onClick={handleClose}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

function flattenObject(
  obj: unknown,
  prefix: string = '',
  result: Record<string, unknown> = {},
): Record<string, unknown> {
  if (obj === null || obj === undefined) {
    result[prefix] = obj;
    return result;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      result[prefix] = '[]';
    } else {
      obj.forEach((item, index) => {
        flattenObject(item, `${prefix}[${index}]`, result);
      });
    }
    return result;
  }

  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      result[prefix] = '{}';
    } else {
      keys.forEach((key) => {
        const newPrefix = prefix ? `${prefix}.${key}` : key;
        flattenObject((obj as Record<string, unknown>)[key], newPrefix, result);
      });
    }
    return result;
  }

  result[prefix] = obj;
  return result;
}

function valueToString(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}
